import React, {Fragment, ReactElement, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardMedia,
    CircularProgress,
    Pagination,
    Toolbar,
    Typography
} from "@mui/material";
import SnackError from "../../components/SnackError";
import {
    MachineApiGet,
    useDeleteMachineByIdMutation,
    useGetMachineQuery,
    usePutMachineByIdMutation,
} from "./machine_api";
import Cancel from "@mui/icons-material/Cancel";
import Delete from "@mui/icons-material/Delete";
import DoNotDisturb from "@mui/icons-material/DoNotDisturb";
import Edit from "@mui/icons-material/Edit";
import Upload from "@mui/icons-material/Upload";
import {SearchBox} from "../../components/SearchBox";
import sortComparator from "../../libs/sortComparator";
import filterFunction from "../../libs/filterFunction";
import pager from "../../libs/pager";
import {Route, Routes, useHref} from "react-router-dom";
import {MachineCreate} from "./MachineCreate";
import {MachineEdit} from "./MachineEdit";
import SortBox from "../../components/SortBox";
import Title from "../navigation/Title";
import usePaginator from "../../hooks/usePaginator";
import {ListCardDataCol, ListGrid} from "../../components/styled";
import useDownloadImage from "../storage/useDownloadImage";
import placeholder from "/assets/imgs/logo.png";
import ImageUploadDialog from "../storage/ImageUploadDialog";

export type MachineItemProps = {
    onDelete: (id: number) => void,
    item: MachineApiGet
    selectable?: boolean;
    onSelect?: (id: number, operationId: number|null) => void;
}

const MachineItem = (props: MachineItemProps) => {
    const {item, selectable, onSelect} = props;


    const [put, {
        isLoading: isUpdating,
        error,
        isError,
    }] = usePutMachineByIdMutation();

    const [uploading, setUploading] = useState(false)

    const onUpload = (path: string) => {
        put({body: {image_url: path}, id: item.id})
        setUploading(false)
    }

    const path = useHref(`./${item.id}/edit`);
    const avatarUrl = useDownloadImage("machines", item.image_url)


    if (!item) {
        return <Box>
            <Alert severity={"error"}>Object not found!</Alert>
        </Box>
    }

    return <Card onClick={() => {
        selectable && onSelect && onSelect(item.id, item.DefaultOperationID);
    }}
                 sx={{
                     display: "flex"
                 }}>
        <CardMedia
            component="img"
            sx={{width: 120, height: 120, minWidth: 120, objectFit: "cover",}}
            image={avatarUrl || placeholder}
            alt={item.Name}
        />
        <ListCardDataCol>
            <CardContent>
                <Typography variant={"subtitle1"}>{item.Code} {!item.Active && "Neaktiven"}</Typography>
                <Typography variant={"h6"}>{item.Name}</Typography>
                <Typography variant={"body1"}>{item.Color}</Typography>
            </CardContent>
            {!selectable && <CardActions>
                <Button onClick={() => setUploading(true)}
                        title={"Naloži sliko"}
                ><Upload fontSize={"small"}/></Button>

                <Button
                    title={item.Active ? "Onemogoči" : "Omogoči"}
                    sx={{
                        color: item.Active ? undefined : "error.main"
                    }} onClick={() => {
                    put({body: {Active: !item.Active}, id: item.id})
                }}>{!item.Active ? <Cancel/> : <DoNotDisturb/>}</Button>

                <Button href={path}
                        title={"Uredi stroj"}
                ><Edit fontSize={"small"}/></Button>
                <Button title="Briši stroj"
                        onClick={() => {
                            props.onDelete(item.id);
                        }}
                ><Delete fontSize={"small"}/> </Button>
            </CardActions>}

            <ImageUploadDialog
                uploading={uploading}
                onUpload={onUpload}
                bucket={"machines"}
                onClose={() => setUploading(false)}
            />
        </ListCardDataCol>
    </Card>

}


export type MachineListProps = {
    list: MachineApiGet[],
    selectable?: boolean;
    onSelect?: (id: number) => void;
}

export const MachineList = (props: MachineListProps) => {
    const {list, onSelect, selectable} = props;

    const [deleteMachine, {
        error: deleteMachineError
    }] = useDeleteMachineByIdMutation();

    const handleDelete = (id: number) => {
        deleteMachine({id});
    }

    return <ListGrid>
        {deleteMachineError && <SnackError error={deleteMachineError}/>}
        {list.map(param => <MachineItem
            key={param.id}
            item={param}
            onSelect={onSelect}
            selectable={selectable}
            onDelete={handleDelete}/>
        )}
    </ListGrid>
}


type MachineControllerProps = {
    pageSize?: number;
    selectable?: boolean;
    onSelect?: (id: number,operationID?: number) => void;
    customFilter?: ReactElement
}

const sortFields = {
    Name: "Naziv",
    Code: "Koda",
    Active: "Aktiven",
}

export const MachineListController = function ({pageSize, selectable, onSelect}: MachineControllerProps) {

    const {
        filter,
        debouncedFilter,
        setFilter,
        page,
        setPage,
        sortField,
        setSortField,
        sortDirection,
        setSortDirection
    } = usePaginator("machine", sortFields,)
    const {data, error, isFetching} = useGetMachineQuery({});

    const path = useHref("./create");
    if (error) {
        return <SnackError error={error}/>;
    }
    if (isFetching || !data) {
        return <CircularProgress/>
    }


    const list: Array<MachineApiGet> = data && data.filter(filterFunction(debouncedFilter, ["Name", "Code", "Active"]))
        .sort(sortComparator(sortDirection, sortField)) || [];
    const [pages, currentPage] = pager(list, page, pageSize);

    return <Fragment>
        <Toolbar sx={{
            display: "flex",
            "&.MuiToolbar-root": {p: 0},
            mb: 1,
            justifyContent: "space-between",
            alignItems: "flex-end",
            flexWrap: "wrap"
        }}>

            {!selectable && <Button href={path}>Ustvari stroj</Button>}

            <Box sx={{display: "flex"}}>
                <SearchBox value={filter} onChange={setFilter}/>
                <SortBox sortDirection={sortDirection} sortField={sortField}
                         onChangeDirection={setSortDirection}
                         onSelectField={setSortField} fields={sortFields}
                />
            </Box>
        </Toolbar>
        {error && <SnackError error={error}/>}

        <MachineList list={currentPage} selectable={selectable} onSelect={onSelect}/>
        <Pagination page={page} onChange={(e, page) => setPage(page)} count={pages}/>
    </Fragment>

}
export type MachineProps = {
    pageSize?: number;
}

export default ({pageSize = 25}: MachineProps) => {

    return <Routes>
        <Route path={"/create"} element={<MachineCreate/>}/>
        <Route path={"/:id/edit"} element={<MachineEdit/>}/>
        <Route path={"/"} element={<Fragment>
            <Title title={"Upravljanje delovnih mest"}/>
            <MachineListController pageSize={pageSize}/>
        </Fragment>}/>
    </Routes>
}
